import { useMemo } from 'react';
import type { ICustomSearchScenario } from '@/api/cms';
import {
  getLanguageCorrectedMessage,
  LANGUAGE_CORRECTIONS,
  TYPO_CORRECTIONS,
  WILDCARD_CORRECTIONS,
} from '../helpers';

const xRegex = /[×*]/g;
const moreThanOneSpaceRegex = /\s+/g;

// ts-ignore allows 'u' flag for emoji support, safe at runtime despite ES5 target.
const emojiRegex =
  // @ts-ignore
  /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{2B50}\u{2B55}\u{1F004}\u{1F0CF}\u{1F18E}\u{1F191}-\u{1F19A}\u{1F1E6}-\u{1F1FF}\u{1F201}\u{1F21A}\u{1F22F}\u{1F232}-\u{1F236}\u{1F238}-\u{1F23A}\u{1F250}\u{1F251}\u{1F300}-\u{1F320}\u{1F32D}-\u{1F335}\u{1F337}-\u{1F37C}\u{1F37E}-\u{1F393}\u{1F3A0}-\u{1F3CA}\u{1F3CF}-\u{1F3D3}\u{1F3E0}-\u{1F3F0}\u{1F3F4}\u{1F3F8}-\u{1F3FF}\u{1F400}-\u{1F43E}\u{1F440}\u{1F442}-\u{1F4FC}\u{1F4FF}-\u{1F53D}\u{1F54B}-\u{1F54E}\u{1F550}-\u{1F567}\u{1F57A}\u{1F595}\u{1F596}\u{1F5A4}\u{1F5FB}-\u{1F64F}\u{1F680}-\u{1F6C5}\u{1F6CC}\u{1F6D0}-\u{1F6D2}\u{1F6D5}-\u{1F6D7}\u{1F6EB}\u{1F6EC}\u{1F6F4}-\u{1F6FC}\u{1F7E0}-\u{1F7EB}\u{1F90C}-\u{1F93A}\u{1F93C}-\u{1F945}\u{1F947}-\u{1F978}\u{1F97A}\u{1F97B}\u{1F97C}-\u{1F9A2}\u{1F9A5}-\u{1F9AA}\u{1F9AE}-\u{1F9CA}\u{1F9CD}-\u{1F9FF}\u{1FA70}-\u{1FA74}\u{1FA78}-\u{1FA7A}\u{1FA80}-\u{1FA86}\u{1FA90}-\u{1FAA8}\u{1FAB0}-\u{1FAB6}\u{1FAC0}-\u{1FAC2}\u{1FAD0}-\u{1FAD6}]/gu;

function useSearchCorrection(
  userQuery: string,
  scenario?: ICustomSearchScenario
) {
  const correction = useMemo(() => {
    let correctedMessage = '';
    let correctedQuery = userQuery
      .replace(xRegex, 'x')
      .replace(moreThanOneSpaceRegex, ' ')
      .replace(emojiRegex, '')
      .trim()
      .toLowerCase();

    const langCorrection = LANGUAGE_CORRECTIONS[correctedQuery];
    const typoCorrection = TYPO_CORRECTIONS[correctedQuery];

    if (langCorrection) {
      correctedMessage = getLanguageCorrectedMessage(langCorrection);
      correctedQuery = langCorrection.value;
    }

    if (typoCorrection) {
      correctedQuery = typoCorrection;
    }

    const wildcardCorrection = WILDCARD_CORRECTIONS.find(({ terms }) =>
      terms.every((term) => correctedQuery.includes(term))
    );

    if (wildcardCorrection) {
      correctedQuery = wildcardCorrection.correction;
    }

    if (scenario?.correctedSearch) {
      correctedQuery = scenario.correctedSearch
        ?.trimEnd()
        ?.trimStart()
        .toLowerCase();
    }

    return { correctedQuery, correctedMessage };
  }, [userQuery, scenario]);

  return correction;
}

export default useSearchCorrection;
